// Blog → Posts list. M5, revised 2026-08-15. Migrated to the console
// component system in M7c / P6.
//
// LIST ONLY. Creating a post is `/blog/new`, editing is `/blog/$id`, and
// categories are their own section under `/blog-categories` (owner request:
// separate pages for add/edit rather than inline panels on the list).
//
// Lives in `.index.tsx`, NOT in the layout file: `_authed.blog.tsx` renders an
// <Outlet/> so the child routes can mount.
//
// Unlike marketing pages (SSR, no draft state), a blog post DOES have
// draft/published — an unfinished article should not be publicly readable, and
// `published_at` is stamped on the first publish only.
//
// ⚠️ Posts are SINGLE-LANGUAGE: `language_code` is the only bilingual
// mechanism, there is no translation overlay. The language column is therefore
// a real property of the post, not a view toggle.

import { Button } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  Icon,
  PageHeader,
  Pagination,
  StatusBadge,
  actionsColumn,
  paginate,
  sortRows,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { deletePost, listPosts, updatePost, type BlogLocale } from '~/lib/blog';

export const Route = createFileRoute('/_authed/blog/')({
  component: BlogListPage,
});

type PostRow = Awaited<ReturnType<typeof listPosts>>['posts'][number];

function BlogListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pendingDelete, setPendingDelete] = useState<PostRow | null>(null);
  const table = useTableState({
    defaultSort: { key: 'title', direction: 'asc' },
    filterKeys: ['lang', 'published'],
    defaultPageSize: 50,
  });

  const lang = (table.filters.lang ?? '') as '' | BlogLocale;
  const published = table.filters.published ?? '';

  const postsQuery = useQuery({
    queryKey: ['blog', 'posts', lang, published],
    queryFn: ({ signal }) =>
      listPosts(
        {
          ...(lang ? { lang } : {}),
          ...(published ? { published: published === 'true' } : {}),
        },
        signal,
      ),
    staleTime: 5_000,
  });

  const setPublished = useMutation({
    mutationFn: ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      updatePost(id, { isPublished }),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      // `published_at` stamps on the FIRST publish only, so re-publishing an
      // old post does not silently move it to the top of the public blog.
      toast(vars.isPublished ? 'Post published' : 'Post moved to drafts', { tone: 'success' });
    },
  });

  const removePost = useMutation({
    mutationFn: (id: string) => deletePost(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      toast('Post deleted', { tone: 'success' });
    },
  });

  const filtered = useMemo(() => {
    const all = postsQuery.data?.posts ?? [];
    const needle = table.q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (p) => p.title.toLowerCase().includes(needle) || p.slug.toLowerCase().includes(needle),
    );
  }, [postsQuery.data, table.q]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, table.sort, {
        title: (p) => p.title,
        slug: (p) => p.slug,
        languageCode: (p) => p.languageCode,
        isPublished: (p) => (p.isPublished ? 1 : 0),
        categoryName: (p) => p.categoryName ?? '',
      }),
    [filtered, table.sort],
  );

  const pageRows = useMemo(
    () => paginate(sorted, table.page, table.pageSize),
    [sorted, table.page, table.pageSize],
  );

  const columns: Column<PostRow>[] = [
    {
      key: 'title',
      header: 'Post',
      sortable: true,
      alwaysVisible: true,
      render: (post) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-admin-900">{post.title}</p>
          <p className="mt-0.5 truncate font-mono text-xs text-admin-500">
            /{post.languageCode === 'en' ? 'en/' : ''}blog/{post.slug}
          </p>
        </div>
      ),
    },
    {
      key: 'categoryName',
      header: 'Category',
      sortable: true,
      hideBelow: 'md',
      render: (post) =>
        post.categoryName ? (
          <span className="text-admin-700">{post.categoryName}</span>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'languageCode',
      header: 'Language',
      sortable: true,
      render: (post) => (
        <StatusBadge
          status="lang"
          label={post.languageCode.toUpperCase()}
          tone={post.languageCode === 'ar' ? 'info' : 'neutral'}
        />
      ),
    },
    {
      key: 'isPublished',
      header: 'Status',
      sortable: true,
      render: (post) => <StatusBadge status={post.isPublished ? 'published' : 'draft'} dot />,
    },
    actionsColumn<PostRow>({
      label: (post) => post.title,
      primary: (post) => ({
        id: 'edit',
        label: 'Edit',
        icon: 'edit',
        onSelect: () => void navigate({ to: '/blog/$id', params: { id: post.id } }),
      }),
      actions: (post) => [
        {
          id: 'view',
          label: 'View live',
          icon: 'external',
          // Arabic slugs stay Arabic in the DB, so the URL must be encoded.
          onSelect: () =>
            window.open(
              `${marketingOrigin()}/${post.languageCode === 'en' ? 'en/' : ''}blog/${encodeURIComponent(post.slug)}`,
              '_blank',
              'noreferrer',
            ),
          disabled: !post.isPublished,
          disabledReason: 'Drafts are not publicly readable.',
        },
        {
          id: 'publish',
          label: post.isPublished ? 'Move to drafts' : 'Publish',
          icon: post.isPublished ? 'eye' : 'checkCircle',
          onSelect: () => setPublished.mutate({ id: post.id, isPublished: !post.isPublished }),
        },
        {
          id: 'delete',
          label: 'Delete post',
          icon: 'trash',
          destructive: true,
          onSelect: () => setPendingDelete(post),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Blog posts"
        description={
          <>
            Each post is written in one language. Arabic posts appear on{' '}
            <code className="rounded bg-admin-100 px-1">/blog</code>, English ones on{' '}
            <code className="rounded bg-admin-100 px-1">/en/blog</code> — never both.
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => void navigate({ to: '/blog-categories' })}>
              Categories
            </Button>
            <Button onClick={() => void navigate({ to: '/blog/new' })}>
              <Icon name="plus" className="h-4 w-4" />
              New post
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(p) => p.id}
        loading={postsQuery.isLoading}
        error={postsQuery.error}
        onRetry={() => void postsQuery.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(p) => void navigate({ to: '/blog/$id', params: { id: p.id } })}
        caption="Blog posts"
        empty={{
          icon: 'page',
          title: table.isFiltered ? 'No posts match these filters' : 'No posts yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Write the first post to get started.',
          action: !table.isFiltered ? (
            <Button onClick={() => void navigate({ to: '/blog/new' })}>Write the first post</Button>
          ) : undefined,
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search posts…"
            filters={[
              {
                key: 'lang',
                label: 'Language',
                value: table.filters.lang ?? '',
                options: [
                  { value: 'ar', label: 'Arabic' },
                  { value: 'en', label: 'English' },
                ],
              },
              {
                key: 'published',
                label: 'Status',
                value: table.filters.published ?? '',
                options: [
                  { value: 'true', label: 'Published' },
                  { value: 'false', label: 'Drafts' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={sorted.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this post"
        description={
          <>
            <span className="font-medium">{pendingDelete?.title}</span> will be removed permanently.
            {pendingDelete?.isPublished && ' It is currently live on the public blog.'}
          </>
        }
        confirmLabel="Delete post"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await removePost.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

/** Where a published post can be previewed. */
function marketingOrigin(): string {
  const host = window.location.hostname;
  if (host === 'admin.fajrarabi.com') return 'https://fajrarabi.com';
  if (host === 'staging-admin.fajrarabi.com') return 'https://staging.fajrarabi.com';
  return 'http://localhost:5173';
}
